import axios from 'axios';
import { loadUserInfo } from './client';
import { getAccessToken, refreshToken, saveTokenCache, clearAuth } from './token';
import { Message } from 'motify'
import aes from '@/utils/security'
import { getSourceUserId } from '@/apis/affair';

// 最大重发次数
const MAX_ERROR_COUNT = 3;

// 当前重发次数
let currentCount = 0;

// 缓存请求队列
const queue = []

// 当前是否缓存刷新状态
let isRefreshing = false

const processQueue = (error, token = null) => {
  queue.forEach(prom => error ? prom.reject(error) : prom.resolve(token))
}

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // api的base_url
  timeout: 10000, // 请求超时时间
});

// request拦截器
service.interceptors.request.use(

  async (config) => {
    // 在发送请求之前做些什么
    let token = config.noToken ? "" : await getAccessToken()

    const headers = {
      Authorization: `Bearer ${token}`,
      ...config.headers,
    }
    config.headers = headers;

    // 判断是否需要加密
    if (config.decrypt) {
      const { current } = await loadUserInfo()

      let { params, data } = config
      if (current && current.id) {
        const res = await getSourceUserId(current.id)
        if (!res.error && res.data) {
          if (params) params.userId = res.data.targetUserId
          if (data) data.userId = res.data.targetUserId
        }
      }

      config.params ? config.params = aes.OAEncryptObject(config.params) : ""
      config.data ? config.data = aes.OAEncryptObject(config.data) : ""
    }

    return config
  },
  (error) => {
    Message.error(error);
    Promise.reject(error);
  },
);

// response拦截器
service.interceptors.response.use(
  async (response) => {
    if (window.out) {
      await handleRefreshToken()
      window.out = false
      return
    }

    // 判断是否需要解密
    if (response.config.decrypt) {
      response = aes.formatRes(response)
    }

    if (response.data.error) {
      Message.error(response.data.message);
    }

    return response.config.complete ? response : response.data
  },
  (error) => {
    const { config, response: { status } } = error
    const originalRequest = config

    if ([401, 403].includes(status)) {
      return handleRefreshToken(originalRequest)
    }

    Message.error(error);
    return Promise.reject(error);
  },
);

// 无感刷新token
async function handleRefreshToken(config) {
  // 判断是否在刷新状态中，防止多个请求重复调用refreshToken
  if (!isRefreshing) {
    console.log('登录已过期，请重新登录');
    // 设置为刷新中
    isRefreshing = true

    // 如果重发次数超过，直接退出登录
    if (currentCount > MAX_ERROR_COUNT) {
      return
    }

    // 增加重试次数
    currentCount += 1;

    try {
      // 请求刷新token
      const tokenData = await refreshToken();

      // 缓存新的token和refreshToken
      saveTokenCache(tokenData)

      // 重置请求计数
      currentCount = 0;

      // 遍历队列，重新请求
      config.headers.Authorization = `Bearer ${tokenData.accessToken}`
      processQueue(null, tokenData.accessToken)
      return service(config)
    } catch (err) {
      Message.error('刷新token失败，请重新登陆')
      processQueue(err, null);
      clearAuth()
    } finally {
      isRefreshing = false
    }
  }

  console.log('token刷新中...');
  // 当前正在尝试刷新token，先返回一个promise阻塞请求并推到请求队列中
  return new Promise((resolve, reject) => {
    queue.push({ resolve, reject })
  }).then(token => {
    config.headers.Authorization = `Bearer ${token}`
    return service(config)
  })
    .catch(err => Promise.reject(err))
}

export default service;
